import React, { FC, useMemo, useState, useEffect } from 'react';
import { Slider } from 'antd';
import { SliderMarks } from 'antd/lib/slider';

import DdPopoverSelector, { DdPopoverSelectorProps } from '~src/shared/selectors/DdPopoverSelector';
import DdHeaderFooterSaveContent from '~src/shared/selectors/DdHeaderFooterSaveContent';
import { OptionItem } from '~src/shared/selectors/DdSearchContent';

import { RangeContainer } from './RangeSelector.sc';

type RangeVal = [number, number];

interface OwnProps {
  value: RangeVal;
  rangeValueType?: string;
  marks?: SliderMarks;
  applyRange: (range: RangeVal) => void;
}

type Props = OwnProps & Pick<DdPopoverSelectorProps, 'triggerIconName' | 'inactiveKey'>;

const RangeSelector: FC<Props> = ({applyRange, inactiveKey, marks = { 0: '0%', 100: '100%' }, rangeValueType = '%', value}) => {
  const [range, setRange] = useState<RangeVal>(value);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [selectedOpt, setSelectedOpt] = useState<OptionItem | undefined>(getSelectedOpt());

  useEffect(() => {
    if (isPopoverVisible) {
      setRange(value);
    }
  }, [isPopoverVisible]);

  function getSelectedOpt() {
    return Array.isArray(range) && range[1] ? { id: '1', name: getRangePreview() } : undefined;
  }

  function getRangePreview() {
    return `${range[0]}${rangeValueType} - ${range[1]}${rangeValueType}`;
  }

  const clearRange = () => {
    setRange([0, 0]);
  };

  const resetFilter = () => {
    clearRange();
    setSelectedOpt(undefined);
    applyRange([0, 0]);
  };

  const doApplyRange = () => {
    setSelectedOpt(getSelectedOpt());
    applyRange(range);
    setIsPopoverVisible(false);
  };

  const updateValues = (val: unknown) => {
    setRange(val as RangeVal);
  };

  const popoverContent = useMemo<React.ReactNode>(() => {
    const selectionInfo = Array.isArray(range) && range[1] ? getRangePreview() : '0% -';
    return (
      <DdHeaderFooterSaveContent
        selectionInfo={selectionInfo}
        isDisplayClear={!!range[1]}
        onClickClear={clearRange}
        onClickSave={doApplyRange}
      >
        <RangeContainer>
          <Slider range value={range} marks={marks} onChange={updateValues} />
        </RangeContainer>
      </DdHeaderFooterSaveContent>
    );
  }, [range]);

  return (
    <DdPopoverSelector
      inactiveKey={inactiveKey}
      inactive={!selectedOpt}
      popoverContent={popoverContent}
      isPopoverVisible={isPopoverVisible}
      togglePopover={setIsPopoverVisible}
      selectedOption={selectedOpt}
      withReset
      resetSelected={resetFilter}
    />
  );
};


export default RangeSelector;
